import { useV1CitiesSuspense } from "@hermes/api";
import { useLocale } from "@hermes/intl";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
  type Params,
  useMatches,
  useNavigate,
  useParams as useRouteParams,
} from "react-router-dom";

import { RoutesIdEnum, useRoutePath } from "#internal/app/router";

import { CityContext } from ".";
import { getCitySlugCookie, setCitySlugCookie } from "./city-cookie";

const useCityRoutePath = () => {
  const { routePath } = useRoutePath();

  return {
    cityRoutePath({
      routeId,
      citySlug,
      cityAreaSlug,
      params,
    }: {
      citySlug: string;
      cityAreaSlug?: string;
      params: Readonly<Params<string>>;
      routeId: RoutesIdEnum;
    }) {
      switch (routeId) {
        case RoutesIdEnum.DOCTORS_LIST:
          return routePath({
            path: "/doctors/:city/:service",
            params: {
              city: citySlug,
              service: params["*"]!,
            },
            query: {
              city_area: cityAreaSlug,
            },
            preserveQuery: true,
          });
        case RoutesIdEnum.CLINICS_LIST:
          return routePath({
            path: "/clinics/:city",
            params: {
              city: citySlug,
            },
            preserveQuery: true,
          });
        case RoutesIdEnum.PROCEDURES_LIST:
          return routePath({
            path: "/procedures/:city",
            params: {
              city: citySlug,
            },
            preserveQuery: true,
          });
        case RoutesIdEnum.CLINIC_PROFILE:
          return routePath({
            path: "/clinics/:city/:slug",
            params: {
              city: citySlug,
              slug: params["slug"]!,
            },
            preserveQuery: true,
          });
        default:
          return undefined;
      }
    },
  };
};
const defaultCityId = import.meta.env.DOQ_DEFAULT_CITY_ID
  ? Number(import.meta.env.DOQ_DEFAULT_CITY_ID)
  : undefined;

export const CityProvider = ({ children }: { children: ReactNode }) => {
  const [locale] = useLocale();
  const { data: citiesData } = useV1CitiesSuspense({ locale: locale.baseName });
  const cities = citiesData.results;

  const [localCitySlug, setLocalCitySlugState] = useState(getCitySlugCookie());

  const setLocalCitySlug = useCallback((slug: string) => {
    setCitySlugCookie(slug);
    setLocalCitySlugState(slug);
  }, []);

  const { cityRoutePath } = useCityRoutePath();
  const navigate = useNavigate();

  const matches = useMatches();
  const outletRoute = matches[matches.length - 1];

  const routeId = outletRoute?.id as unknown as RoutesIdEnum;

  const params = useRouteParams();
  const citySlug = params["city"];

  const routeCity = useMemo(
    () => cities.find((city) => city.slug === citySlug),
    [citySlug, cities],
  );
  const storedCity = useMemo(
    () => cities.find((city) => city.slug === localCitySlug),
    [localCitySlug, cities],
  );
  const defaultCity = useMemo(() => {
    const target =
      cities.find((city) => city.id === defaultCityId) ?? cities[0];
    if (!target) {
      throw new Error("Could not resolve default city");
    }

    return target;
  }, [cities]);

  return (
    <CityContext.Provider
      value={{
        routeCity,
        defaultCity,
        storedCity,
        cities,
        /**
         * City is determined by priority:
         *
         * 1. From route, where applicable
         * 2. Stored, from local storage slug
         * 3. Default, from default city id env
         */
        city: routeCity ?? storedCity ?? defaultCity,
        changeCityBySlug: (citySlug?: string, cityAreaSlug?: string) => {
          if (!citySlug) return;
          const path = cityRoutePath({
            citySlug,
            params,
            routeId,
            cityAreaSlug,
          });

          if (path) navigate(path);
          setLocalCitySlug(citySlug);
        },
        resolveCityById: useCallback(
          (id?: number) => {
            return cities.find((city) => city.id === id);
          },
          [cities],
        ),
      }}
    >
      {children}
    </CityContext.Provider>
  );
};
